import React, { useState } from 'react';
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {LogOut, Settings, Bell, LayoutDashboard, Users, Calendar, FileText, Home, Menu, X, HeadphonesIcon} from "lucide-react";
import {logoutUser} from "../../services/auth/authActions.js";

const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

const Navbar = ({children}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(state => state.authSlice.userInfo);
    const token = useSelector(state => state.authSlice.accessToken);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const tokenPayload = token ? parseJwt(token) : null;
    const userRole = user?.role || tokenPayload?.role || 'USER';

    const handleLogout = () => {
        logoutUser();
    }

    const navItems = [
        {
            path: '/dashboard',
            title: 'Panel',
        },
        {
            path: '/pacientes',
            title: 'Historiales Médicos',
        },
        {
            path: '/turnos',
            title: 'Turnos',
            icon: Calendar
        },
        {
            path: '/balance',
            title: 'Balance Personal',
        },
        {
            path: '/tareas',
            title: 'Tareas',
        },
        {
            path: '/disponibilidad',
            title: 'Disponibilidad',
        }
    ];

    if (userRole === 'ADMIN' || userRole === 'SUPERADMIN') {
        navItems.splice(3, 0, {
            path: '/balance-grupal',
            title: 'Balance Grupal',
        });
        navItems.push({
            path: '/profesionales',
            title: 'Personal',
        });
        navItems.push({
            path: '/contenido',
            title: 'Contenido',
        });
    }

    return (
        <div className="flex min-h-screen bg-[#F0F4F8] font-sans">
            {/* Main App Container */}
            <div className="flex-1 bg-white flex flex-col min-h-screen">
                
                {/* Top Navigation Bar */}
                <header className="sticky top-0 z-[100] h-20 flex items-center justify-between px-8 border-b border-gray-100 shrink-0 bg-white/95 backdrop-blur-sm">
                    {/* Logo */}
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 -ml-2 text-gray-500 hover:text-gray-900 transition-colors"
                        >
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                        <img src="/images/pausesLogo.png" alt="Pauses Logo" className="h-16 w-auto object-contain scale-150 ml-4" />
                    </div>

                    {/* Centered Navigation */}
                    <nav className="hidden md:flex items-center gap-2">
                        {navItems.map((item) => {
                            const isExact = location.pathname === item.path;
                            const isTurnosActive = (item.path === '/turnos' || item.path === '/dashboard') && 
                                (location.pathname === '/turnos' || location.pathname === '/dashboard');
                            const isActive = item.path === '/turnos' ? isTurnosActive : (item.path === '/dashboard' ? isExact : isExact);
                            const Icon = item.icon;

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`px-4 py-2 rounded-xl text-[14px] font-bold transition-all duration-200 flex items-center gap-2 ${
                                        isActive
                                            ? 'border-2 border-[#1D68F2] text-[#1D68F2] bg-white shadow-xs'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-2 border-transparent'
                                    }`}
                                >
                                    {Icon && <Icon size={16} className={isActive ? 'text-[#1D68F2]' : 'text-gray-400'} />}
                                    {item.title}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Right Actions */}
                    <div className="flex items-center gap-4">
                        {/* Ayuda / Soporte matching mockup */}
                        <button 
                            onClick={() => window.open('https://wa.me/3245937358', '_blank')}
                            className="hidden md:flex items-center gap-2 px-3.5 py-1.5 rounded-xl border border-amber-300 bg-[#FFFBEB] text-[#92400E] text-xs font-bold hover:bg-amber-100 transition-colors shadow-xs"
                            title="Ayuda / Soporte"
                        >
                            <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center text-amber-800">
                                <HeadphonesIcon size={12} />
                            </div>
                            <span>Ayuda / Soporte</span>
                        </button>

                        <div className="w-px h-6 bg-gray-200 hidden md:block"></div>

                        <Link 
                            to="/" 
                            className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                            title="Ir a la web del sistema"
                        >
                            <Home size={18} />
                            <span className="text-sm font-semibold hidden sm:inline">Inicio</span>
                        </Link>
                        <button 
                            onClick={() => navigate('/perfil')} 
                            className="text-gray-400 hover:text-[#3B82F6] transition-colors"
                            title="Configuración de Perfil"
                        >
                            <Settings size={20} />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 transition-colors relative" title="Notificaciones">
                            <Bell size={20} />
                            <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
                        </button>
                        <div className="w-px h-6 bg-gray-200 mx-1"></div>
                        
                        {/* User Avatar Action */}
                        <div 
                            onClick={() => navigate('/perfil')}
                            className="flex items-center gap-2.5 cursor-pointer group hover:bg-gray-50 px-2.5 py-1.5 rounded-xl transition-all border border-transparent hover:border-gray-200"
                            title="Ver mi perfil de usuario"
                        >
                            <div className="h-9 w-9 rounded-full bg-[#1D68F2] text-white font-black text-xs flex items-center justify-center shadow-xs">
                                {(() => {
                                    const n = user?.name || user?.nombre || 'TM';
                                    const parts = n.trim().split(/\s+/).filter(Boolean);
                                    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
                                    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                                })()}
                            </div>
                            <div className="hidden lg:flex flex-col text-left">
                                <span className="text-xs font-black text-gray-800 leading-tight">
                                    {user?.name || user?.nombre || 'Tomás Manazza'}
                                </span>
                                <span className="text-[10px] text-gray-500 font-bold">
                                    {user?.role === 'ADMIN' ? 'Administrador' : (user?.role || 'Profesional')}
                                </span>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Mobile Menu Overlay */}
                <div 
                    className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                ></div>

                {/* Mobile Menu Drawer */}
                <div 
                    className={`fixed top-0 left-0 h-full w-[280px] bg-white z-50 transform transition-transform duration-300 ease-in-out md:hidden shadow-2xl flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
                >
                    <div className="flex items-center justify-between p-6 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <img src="/images/pausesLogo.png" alt="Pauses Logo" className="h-16 w-auto object-contain scale-150 ml-4" />
                        </div>
                        <button onClick={() => setIsMobileMenuOpen(false)} className="text-gray-500 hover:text-gray-900 transition-colors">
                            <X size={24} />
                        </button>
                    </div>
                    <nav className="flex flex-col p-4 gap-2 overflow-y-auto flex-1">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={`px-4 py-3 rounded-xl text-[15px] font-semibold transition-all ${
                                        isActive
                                            ? 'bg-[#13263E] text-white shadow-md shadow-[#13263E]/20'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                >
                                    {item.title}
                                </Link>
                            );
                        })}
                        <div className="h-px bg-gray-100 w-full my-4"></div>
                        <Link 
                            to="/perfil" 
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                        >
                            <Settings size={20} /> Mi Perfil
                        </Link>
                        <Link 
                            to="/" 
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                        >
                            <Home size={20} /> Ir al Inicio
                        </Link>
                        <button 
                            onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-semibold text-red-500 hover:bg-red-50 transition-colors mt-auto mb-4"
                        >
                            <LogOut size={20}/> Cerrar Sesión
                        </button>
                    </nav>
                </div>

                {/* Main Content Area */}
                <main className="flex-1 bg-white">
                    <div className="min-h-full w-full">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Navbar;